<template>
  <div class="post-list-container">
    <div class="filter-section">
      <div class="search-wrapper">
        <el-input 
          v-model="searchKeyword" 
          placeholder="搜索帖子..." 
          class="search-input"
          @input="handleSearch"
          clearable
        >
        </el-input>
        <el-button 
          type="primary" 
          icon="Search" 
          class="search-button"
          @click="handleSearch"
        >
          搜索
        </el-button>
      </div>
    </div>
    
    <div class="posts-container">
      <div 
        v-for="post in posts" 
        :key="post.id" 
        class="post-card"
        @click="goToPostDetail(post.id)"
        style="cursor: pointer;"
      >
        <div class="post-header">
          <el-avatar 
            :size="40" 
            :src="post.userAvatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" 
            class="author-avatar"
          ></el-avatar>
          <div class="post-author-info">
            <h4>{{ post.name }}</h4>
            <span class="post-time">{{ formatDate(post.createTime) }}</span>
          </div>
          <div class="user-actions">
            <el-button 
              v-if="computedUserId !== post.userId"
              :type="post.isFollowed ? 'success' : 'default'"
              size="small"
              class="follow-btn"
              @click.stop="toggleFollow(post)"
            >
              {{ getFollowButtonText(post) }}
            </el-button>
          </div>
        </div>
        
        <div class="post-cover" v-if="post.coverImage">
          <img :src="post.coverImage" alt="帖子封面" />
        </div>
        
        <div class="post-content">
          <h3 class="post-title">{{ post.title }}</h3>
          <p class="post-text">{{ post.content }}</p>
        </div>
        
        <!-- 操作按钮 -->
        <div class="post-actions">
          <el-button 
            :icon="ThumbUpIcon" 
            size="small" 
            :type="post.isLiked ? 'danger' : 'default'"
            class="action-btn"
            @click.stop="toggleLike(post)"
          >
            {{ post.isLiked ? '已点赞' : '点赞' }} ({{ post.likeCount }})
          </el-button>
          <el-button 
            :icon="ChatLineRoundIcon" 
            size="small" 
            type="default"
            class="action-btn"
            @click.stop="goToPostDetail(post.id)"
          >
            评论 ({{ post.commentCount }})
          </el-button>
          <el-button 
            :icon="StarIcon" 
            size="small" 
            :type="post.isFavorited ? 'warning' : 'default'"
            class="action-btn"
            @click.stop="toggleFavorite(post)"
          >
            {{ post.isFavorited ? '已收藏' : '收藏' }} ({{ post.favoriteCount }})
          </el-button>
          <el-button 
            :icon="ShareIcon" 
            size="small" 
            type="default"
            class="action-btn"
            @click.stop="handleShare(post)"
          >
            分享
          </el-button>
          
          <!-- 编辑/删除按钮，仅当帖子发布者是当前用户时显示 -->
          <div v-if="computedUserId === post.userId" class="user-actions">
            <el-button 
              size="small" 
              type="primary"
              class="action-btn edit-btn"
              @click.stop="openEditModal(post.id)"
            >
              编辑
            </el-button>
            <el-button 
              size="small" 
              type="danger"
              class="action-btn delete-btn"
              @click.stop="confirmDeletePost(post.id)"
            >
              删除
            </el-button>
          </div>
        </div>
        
        <!-- 热门评论展示 -->
        <div 
          v-if="post.hotComment" 
          class="hot-comment"
        >
          <div class="comment-header">
            <el-avatar 
              :size="24" 
              :src="post.hotComment.userAvatar" 
              class="comment-author-avatar"
            ></el-avatar>
            <span class="comment-author">{{ post.hotComment.name }}</span>
          </div>
          <p class="comment-content">{{ post.hotComment.content }}</p>
          <span class="comment-time">{{ formatDate(post.hotComment.createTime) }}</span>
        </div>
      </div>
      
      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[5, 10, 20, 50]"
          :total="totalRecords"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
    
    <!-- 编辑帖子模态框 -->
    <EditPostModal
      v-model="editDialogVisible"
      :post-id="editingPostId"
      @save-success="onEditSuccess"
      @close="onModalClose"
    />
    
    <!-- 空状态 -->
    <div v-if="posts.length === 0 && !loading" class="empty-state">
      <el-empty description="暂无帖子，快发布第一个帖子吧！" />
    </div>
    
    <!-- 错误提示 -->
    <div v-if="error" class="error-state">
      <el-alert
        title="加载失败"
        type="error"
        :description="errorMessage"
        show-icon
        closable
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// 临时从 vue 导入 defineEmits 以满足 ESLint 检查
import { ref, onMounted, computed, defineEmits, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { postList, likePost as apiLikePost, favoritePost as apiFavoritePost, type PostItem, followUser, likePost, favoritePost, deletePost } from '@/user/api/post';
import { useStore } from 'vuex';
import { 
  ChatLineRound,
  Star,
  ThumbUp,
  Share
} from '@element-plus/icons-vue';
import { formatPostDate } from '@/utils/date';
import EditPostModal from './EditPostModal.vue'; // 引入编辑模态框组件

// 定义图标常量，供模板使用
const ThumbUpIcon = ThumbUp;
const ChatLineRoundIcon = ChatLineRound;
const StarIcon = Star;
const ShareIcon = Share;

// 定义 emits
interface Emits {
  'need-login': [];
}

const emit = defineEmits<Emits>();

// 使用路由和状态管理
const router = useRouter();
const store = useStore();

// 计算属性获取当前登录用户的ID
const computedUserId = computed(() => {
  const userInfo = store.state.userInfo;
  return userInfo?.id || null;
});

// 添加点击帖子跳转到详情页的方法
const goToPostDetail = (postId: number) => {
  // 跳转到帖子详情页
  router.push(`/post/${postId}`);
};


// 响应式数据
const posts = ref<PostItem[]>([]);
const currentPage = ref(1);
const pageSize = ref(10);
const totalRecords = ref(0);
const loading = ref(false);
const error = ref(false);
const errorMessage = ref('');
// 添加一个标志来跟踪是否有请求正在进行
const isRequestPending = ref(false);

// 搜索关键词
const searchKeyword = ref('');

// 编辑模态框相关状态
const editDialogVisible = ref(false);
const editingPostId = ref<number | null>(null);

// 帖子列表请求参数
const requestParams = ref({
  page: 1,
  pageSize: 10,
  keyword: ''
});

// 格式化日期
const formatDate = (dateStr: string) => {
  return formatPostDate(dateStr);
};

const fetchPosts = async () => {
  // 如果已经有请求正在进行，跳过本次请求
  if (isRequestPending.value) {
    return;
  }
  
  try {
    isRequestPending.value = true;
    error.value = false;
    loading.value = true;
    const params = {
      ...requestParams.value,
      page: currentPage.value,
      pageSize: pageSize.value,
      keyword: searchKeyword.value
    };
    
    console.log('Fetching posts with params:', params);
    
    const response = await postList(params);
    
    console.log('Response received:', response);
    
    // 根据控制台输出，API 直接返回了 {total: 6, records: Array(6) } 格式
    // 没有 code 和 msg 字段，也没有包装在 data 字段中
    // 使用安全的方式检查属性
    if (response && 
        Object.prototype.hasOwnProperty.call(response, 'records') && 
        Object.prototype.hasOwnProperty.call(response, 'total')) {
      // 直接使用返回的数据
      totalRecords.value = response.total;
      posts.value = response.records || [];
    } else {
      console.error('Response error or invalid format:', response);
      ElMessage.error('获取帖子列表失败：数据格式不符合预期');
    }
  } catch (err: any) {
    console.error('获取帖子列表失败:', err);
    
    error.value = true;
    
    // 更详细的错误信息
    if (err.response) {
      // 服务器响应了错误状态码
      console.error(`HTTP ${err.response.status}: ${err.response.statusText}`);
      
      if (err.response.status === 403) {
        errorMessage.value = '权限不足，可能需要登录或令牌已过期，请重新登录';
        // 提示用户重新登录
        ElMessage.error('权限不足，请重新登录');
      } else {
        errorMessage.value = `服务器错误: ${err.response.status}`;
      }
    } else if (err.request) {
      // 请求已发出但没有收到响应
      console.error('请求已发出但未收到响应:', err.request);
      errorMessage.value = '网络连接失败，请检查网络';
    } else {
      // 其他错误
      console.error('请求配置错误:', err.message);
      errorMessage.value = '请求配置错误';
    }
    
    // 设置错误消息后显示错误状态
    ElMessage.error(errorMessage.value || '获取帖子列表失败');
  } finally {
    loading.value = false;
    isRequestPending.value = false;
  }
};

// 每页大小改变时的回调
const handleSizeChange = (newSize: number) => {
  pageSize.value = newSize;
  currentPage.value = 1; // 重置为第一页
  
  fetchPosts();
};

// 页码改变时的回调
const handleCurrentChange = (newPage: number) => {
  currentPage.value = newPage;
  
  fetchPosts();
};

// 处理搜索
const handleSearch = () => {
  currentPage.value = 1; // 搜索时回到第一页
  
  fetchPosts();
};

// 处理点赞/取消点赞
const toggleLike = async (post: PostItem) => {
  try {
    // 如果用户未登录，触发需要登录事件
    if (!store.getters.isAuthenticated) {
      emit('need-login');
      return;
    }

    // 调用API进行点赞/取消点赞
    const response = await likePost(post.id);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await fetchPosts();
      ElMessage.success(post.isLiked ? '已取消点赞' : '已点赞');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('点赞操作失败:', error);
    ElMessage.error('点赞操作失败，请稍后再试');
  }
};

// 处理收藏/取消收藏
const toggleFavorite = async (post: PostItem) => {
  try {
    // 如果用户未登录，触发需要登录事件
    if (!store.getters.isAuthenticated) {
      emit('need-login');
      return;
    }

    // 调用API进行收藏/取消收藏
    const response = await favoritePost(post.id);

    if (response && response.code === 1) {
      // 操作成功，重新加载数据以获取最新状态
      await fetchPosts();
      ElMessage.success(post.isFavorited ? '已取消收藏' : '已收藏');
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('收藏操作失败:', error);
    ElMessage.error('收藏操作失败，请稍后再试');
  }
};

// 处理关注/取消关注
const toggleFollow = async (post: PostItem) => {
  try {
    // 如果用户未登录，触发需要登录事件
    if (!store.getters.isAuthenticated) {
      emit('need-login');
      return;
    }

    // 确保post对象存在
    if (!post) {
      ElMessage.error('用户信息不存在');
      return;
    }

    // 调用API进行关注/取消关注
    const response = await followUser(post.userId);

    if (response && response.code === 1) {
      let actionText;
      
      if (response.data) {
        // 根据API返回的新状态判断应该显示什么提示
        const newFollowStatus = response.data.isFollowed;
        actionText = newFollowStatus === 1 ? '关注用户成功' : '已取消关注';
      } else {
        // 如果响应中没有数据，根据当前状态翻转来推断新状态
        const newFollowStatus = post.isFollowed ? 0 : 1;
        actionText = newFollowStatus === 1 ? '关注用户成功' : '已取消关注';
      }
      
      // 操作成功，重新加载数据以获取最新状态
      await fetchPosts();
      ElMessage.success(actionText);
    } else {
      ElMessage.error(response.msg || '操作失败');
    }
  } catch (error: any) {
    console.error('关注操作失败:', error);
    ElMessage.error('关注操作失败，请稍后再试');
  }
};

// 处理分享
const handleShare = (post: PostItem) => {
  // 创建分享链接
  const shareUrl = `${window.location.origin}/post/${post.id}`;
  const shareText = `推荐这篇帖子：${post.title}`;

  // 尝试使用 Web Share API
  if (navigator.share) {
    navigator.share({
      title: post.title,
      text: shareText,
      url: shareUrl
    }).catch(console.error);
  } else {
    // 如果不支持 Web Share API，则复制链接到剪贴板
    navigator.clipboard.writeText(shareUrl).then(() => {
      ElMessage.success('链接已复制到剪贴板');
    }).catch(console.error);
  }
};

// 确认删除帖子
const confirmDeletePost = async (postId: number) => {
  try {
    await ElMessageBox.confirm(
      '确定要删除这个帖子吗？此操作不可撤销。',
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    // 调用删除帖子的API
    const response = await deletePost(postId);
    
    if (response.code === 1) {
      ElMessage.success('帖子删除成功');
      // 重新加载帖子列表
      await fetchPosts();
    } else {
      ElMessage.error(response.msg || '删除帖子失败');
    }
  } catch (error) {
    // 用户取消删除或发生错误
    if (error !== 'cancel') {
      console.error('删除帖子失败:', error);
      ElMessage.error('删除帖子失败');
    }
  }
};

// 打开编辑模态框
const openEditModal = (postId: number) => {
  // 先重置postId，然后在下一个tick设置新值，确保watch触发
  editingPostId.value = null;
  nextTick(() => {
    editingPostId.value = postId;
    editDialogVisible.value = true;
  });
};

// 编辑成功后的回调
const onEditSuccess = () => {
  editDialogVisible.value = false;
  editingPostId.value = null;
  fetchPosts(); // 重新加载帖子列表
};

// 模态框关闭回调
const onModalClose = () => {
  editingPostId.value = null; // 重置postId
};

// 页面加载
onMounted(() => {
  fetchPosts();
});

// 获取关注按钮的文本
const getFollowButtonText = (post: any) => {
  // 检查post是否存在
  if (!post) {
    return ' 👤 关注';
  }

  // 互相关注：当 isFollowed=1 且 isFollowerd=1 时，显示"互相关关"和爱心emoji
  if (post.isFollowed === 1 && post.isFollowerd === 1) {
    return ' ❤️ 互相关注';
  }
  // 回关：当 isFollowed=0 且 isFollowerd=1 时，显示"回关"和招手emoji
  else if (post.isFollowed === 0 && post.isFollowerd === 1) {
    return ' 👋 回关';
  }
  // 已关注：当 isFollowed=1 且 isFollowerd=0 时，显示"已关注"和对勾emoji
  else if (post.isFollowed === 1 && post.isFollowerd === 0) {
    return ' ✅ 已关注';
  }
  // 未关注：当两者均为 0 时，显示"关注"和头像emoji
  else {
    return ' 👤 关注';
  }
};

</script>

<style scoped>
.post-list-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Nunito', 'Quicksand', 'Open Sans', sans-serif;
}

.section-header {
  text-align: center;
  margin-bottom: 25px;
}

.section-header h2 {
  font-size: 28px;
  color: #5a3e36;
  margin-bottom: 10px;
  font-family: 'Quicksand', cursive;
}

.section-header p {
  color: #8b6d4b;
  font-size: 16px;
}

.filter-section {
  margin-bottom: 25px;
  text-align: center;
}

.search-wrapper {
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  border-radius: 50px;
  overflow: hidden;
  border: 2px solid #d4a976;
  box-shadow: 0 4px 12px rgba(212, 169, 118, 0.2);
}

.search-input {
  flex: 1;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: 50px 0 0 50px;
  padding-left: 20px;
  border: none;
  box-shadow: none;
}

.search-button {
  border-radius: 0 50px 50px 0;
  background-color: #d4a976;
  border-color: #d4a976;
  color: white;
  font-weight: 600;
  padding: 0 20px;
  transition: all 0.3s ease;
}

.search-button:hover {
  background-color: #c89d6a;
  border-color: #c89d6a;
  transform: scale(1.02);
}

.search-button:active {
  transform: scale(0.98);
}

.posts-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.post-card {
  background: #fffaf0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 6px 16px rgba(139, 109, 75, 0.1);
  border: 1px solid #e9dcc8;
  transition: all 0.3s ease;
}

.post-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 24px rgba(139, 109, 75, 0.15);
}

.post-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.author-avatar {
  border: 2px solid #d4a976;
}

.post-author-info h4 {
  margin: 0;
  color: #5a3e36;
  font-size: 16px;
  font-weight: 600;
}

.post-time {
  font-size: 13px;
  color: #a8977a;
}

.user-actions {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.edit-btn,
.delete-btn {
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  gap: 4px;
}

.edit-btn {
  background-color: #409eff;
  color: white;
  border-color: #409eff;
}

.edit-btn:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.2);
}

.delete-btn {
  background-color: #f56c6c;
  color: white;
  border-color: #f56c6c;
}

.delete-btn:hover {
  background-color: #f78989;
  border-color: #f78989;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(245, 108, 108, 0.2);
}

.follow-btn {
  margin-left: auto;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  background-color: #f8f3eb;
  color: #8b6d4b;
  border: 1px solid #e9dcc8;
}

.follow-btn:hover {
  background-color: #f0e6d4;
  color: #7a5e3a;
  border: 1px solid #d4a976;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.follow-btn.el-button--success {
  background-color: #e8f5e9;
  color: #4caf50;
  border: 1px solid #c8e6c9;
}

.follow-btn.el-button--success:hover {
  background-color: #c8e6c9;
  color: #388e3c;
  border: 1px solid #a5d6a7;
}

.post-cover {
  width: 100%;
  aspect-ratio: 16 / 9; /* 设置宽高比为16:9 */
  margin-bottom: 15px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}

.post-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 保持图片比例填充整个容器 */
  display: block;
}

.post-content {
  margin: 15px 0;
}

.post-title {
  font-size: 18px;
  color: #5a3e36;
  margin: 0 0 10px 0;
  font-weight: 600;
}

.post-text {
  color: #7d6e63;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-actions {
  display: flex;
  gap: 10px;
  margin: 15px 0;
  padding: 10px 0;
  border-top: 1px dashed #d4c19c;
  flex-wrap: wrap;
}

.action-btn {
  margin-right: 8px;
  margin-bottom: 5px;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-btn.el-button--default {
  background-color: #f8f3eb;
  color: #8b6d4b;
  border: 1px solid #e9dcc8;
}

.action-btn.el-button--default:hover {
  background-color: #f0e6d4;
  color: #7a5e3a;
  border: 1px solid #d4a976;
}

.action-btn.el-button--danger {
  background-color: #ffebee;
  color: #e91e63;
  border: 1px solid #ffcdd2;
}

.action-btn.el-button--danger:hover {
  background-color: #f8bbd0;
  color: #c2185b;
  border: 1px solid #e91e63;
}

.action-btn.el-button--warning {
  background-color: #fff8e1;
  color: #ff8f00;
  border: 1px solid #ffecb3;
}

.action-btn.el-button--warning:hover {
  background-color: #ffecb3;
  color: #f57f00;
  border: 1px solid #ffd54f;
}

.action-btn.el-button--success {
  background-color: #e8f5e9;
  color: #4caf50;
  border: 1px solid #c8e6c9;
}

.action-btn.el-button--success:hover {
  background-color: #c8e6c9;
  color: #388e3c;
  border: 1px solid #a5d6a7;
}

.hot-comment {
  background: #f8f3eb;
  border-radius: 12px;
  padding: 15px;
  margin-top: 15px;
  border-left: 4px solid #d4a976;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.comment-author {
  font-weight: 600;
  color: #5a3e36;
  font-size: 14px;
}

.comment-content {
  color: #7d6e63;
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.comment-time {
  font-size: 12px;
  color: #a8977a;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 25px;
  padding: 15px 0;
}

/* 使用深度选择器确保样式应用到分页组件 */
.pagination-container :deep(.el-pagination) {
  --el-pagination-font-size: 14px;
  display: inline-flex;
  align-items: center;
}

.pagination-container :deep(.el-pager li) {
  background-color: #fffaf0;
  border: 1px solid #e9dcc8;
  color: #5a3e36;
  margin: 0 4px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.pagination-container :deep(.el-pager li:hover) {
  color: #d4a976;
  border-color: #d4a976;
}

.pagination-container :deep(.el-pager li.active) {
  background-color: #d4a976;
  color: white;
  border-color: #d4a976;
}

.pagination-container :deep(.el-pager li:not(.active):hover) {
  color: #d4a976;
}

.pagination-container :deep(.btn-prev),
.pagination-container :deep(.btn-next) {
  background-color: #fffaf0;
  border: 1px solid #e9dcc8;
  border-radius: 8px;
  color: #5a3e36;
  margin: 0 4px;
}

.pagination-container :deep(.btn-prev:hover),
.pagination-container :deep(.btn-next:hover) {
  color: #d4a976;
  border-color: #d4a976;
}

.pagination-container :deep(.el-pagination__sizes) {
  margin-right: 10px;
}

.pagination-container :deep(.el-select .el-input__wrapper) {
  border-radius: 8px;
  border: 1px solid #e9dcc8;
  box-shadow: 0 2px 4px rgba(139, 109, 75, 0.1);
}

.pagination-container :deep(.el-pagination__jump) {
  margin-left: 10px;
}

.pagination-container :deep(.el-pagination__total) {
  color: #8b6d4b;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.error-state {
  margin-top: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .post-list-container {
    padding: 15px 10px;
  }
  
  .section-header h2 {
    font-size: 22px;
  }
  
  .post-card {
    padding: 15px;
  }
  
  .post-actions {
    justify-content: center;
  }
  
  .filter-section {
    padding: 0 10px;
  }
  
  .search-wrapper {
    max-width: 100%;
    margin: 0 10px;
  }
  
  .search-button {
    padding: 0 15px;
  }
}

@media (max-width: 480px) {
  .search-wrapper {
    flex-direction: column;
    border-radius: 12px;
  }
  
  .search-input :deep(.el-input__wrapper) {
    border-radius: 12px 12px 0 0;
    text-align: center;
  }
  
  .search-button {
    border-radius: 0 0 12px 12px;
    width: 100%;
  }
  
  .post-actions {
    flex-direction: column;
  }
  
  .header-nav {
    padding: 0 5px;
  }
  
  .nav-item span {
    display: none;
  }
  
  .nav-item {
    margin-right: 10px;
    padding: 10px;
  }
  
  .section-header h2 {
    font-size: 20px;
  }
}
</style>